import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import NumberedPagination from "../../../shared/ui/Pagination/NumberedPagination";
import Select from "../../../shared/ui/Field/Select";
import { messageFromError } from "../../../shared/api/drfErrors";
import {
  fetchCompany,
  fetchMembers,
  fetchMyCompany,
} from "../../companyWorkspace/api/companyWorkspace";
import {
  isAdminMembership,
  isActiveMembership,
  memberDisplayName,
} from "../../companyWorkspace/lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../../companyWorkspace/lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../../companyWorkspace/lib/companyFocusRevalidation";
import CompanyPageHeader from "../../companyWorkspace/ui/CompanyPageHeader";

import {
  assignCompanyClient,
  fetchCompanyClientsUnassigned,
} from "../api/companyClients";
import {
  clientDisplayName,
  isCorporateClientsAvailable,
  statusLabel,
  typeLabel,
} from "../lib/clientLabels";

function unwrapPage(data) {
  if (Array.isArray(data)) {
    return { results: data, count: data.length, next: null, previous: null };
  }
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    count: typeof data?.count === "number" ? data.count : 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
  };
}

function CompanyClientsUnassignedContent() {
  const { companyId } = useParams();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);

  const [page, setPage] = useState(1);
  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [rows, setRows] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [count, setCount] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);
  const [state, setState] = useState("loading");
  const stateRef = useRef(state);
  const [writesSuspended, setWritesSuspended] = useState(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [assignDraft, setAssignDraft] = useState({});

  const companyMatches =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const authorityMatches = companyMatches && actorMatches;
  const canManage =
    authorityMatches && isAdminMembership(company, membership);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!companyId) {
      setState("not-found");
      return;
    }
    const keepVisible =
      background &&
      (stateRef.current === "ready" || stateRef.current === "unavailable");
    const op = scopeGate.beginRead({ companyId, actorId });
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
      setError(null);
      setActionError(null);
    }
    try {
      const [companyData, mine] = await Promise.all([
        fetchCompany(companyId),
        fetchMyCompany(),
      ]);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const mineMatches =
        mine?.company?.id != null && sameCompanyId(mine.company.id, companyId)
          ? mine.membership
          : null;
      setCompany(companyData);
      setMembership(mineMatches);
      setAuthorityActorId(actorId);

      if (!isActiveMembership(companyData, mineMatches)) {
        setRows([]);
        setAssignees([]);
        setAssignDraft({});
        setState("denied");
        return;
      }
      if (!isCorporateClientsAvailable(companyData)) {
        setRows([]);
        setAssignees([]);
        setAssignDraft({});
        setState("unavailable");
        return;
      }
      if (!isAdminMembership(companyData, mineMatches)) {
        setRows([]);
        setAssignees([]);
        setAssignDraft({});
        setState("denied");
        return;
      }

      const [pageData, roster] = await Promise.all([
        fetchCompanyClientsUnassigned(companyId, {
          page,
          page_size: 20,
        }),
        fetchMembers(companyId, "active"),
      ]);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const unwrapped = unwrapPage(pageData);
      setRows(unwrapped.results);
      setCount(unwrapped.count);
      setNextUrl(unwrapped.next);
      setPrevUrl(unwrapped.previous);
      const eligible = (Array.isArray(roster) ? roster : []).filter(
        (m) => m.status === "active" && (m.role === "member" || m.role === "admin"),
      );
      setAssignees(eligible);
      revalidationConfirmed = true;
      setActionError((current) =>
        current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
      );
      setState("ready");
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (keepVisible && status !== 403 && status !== 404) {
        setActionError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
        return;
      }
      setRows([]);
      if (status === 404) setState("not-found");
      else if (status === 403) setState("denied");
      else {
        setError(messageFromError(err));
        setState("error");
      }
    } finally {
      settleRevalidationWrites({
        keepVisible: keepVisible,
        confirmed: revalidationConfirmed,
        token: backgroundToken,
        tokenAtStart,
        tokenRef: revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      });
    }
  }, [actorId, companyId, page, scopeGate]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({ companyId, actorId });
    busyOpRef.current = null;
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    setRows([]);
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyOpRef.current = null;
    };
  }, [companyId, actorId, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready" || state === "unavailable",
    busy: busyId != null,
    onRevalidate: () => load({ background: true }),
  });

  const handleAssign = async (clientId) => {
    if (!canManage || busyOpRef.current || writesSuspendedRef.current) return;
    const userId = assignDraft[clientId];
    if (!userId) return;
    const op = scopeGate.beginMutation({
      companyId,
      actorId,
      resourceId: clientId,
    });
    busyOpRef.current = op;
    setBusyId(clientId);
    setActionError(null);
    try {
      await assignCompanyClient(companyId, clientId, Number(userId));
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      await load();
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (status === 403 || status === 404) {
        setRows([]);
        setAssignees([]);
        setAssignDraft({});
        setActionError(null);
        setState(status === 404 ? "not-found" : "denied");
        return;
      }
      setActionError(messageFromError(err));
    } finally {
      if (busyOpRef.current === op) {
        busyOpRef.current = null;
        setBusyId(null);
      }
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / 20) || 1);
  const assigneeOptions = [
    { value: "", label: "Обрати рієлтора" },
    ...assignees.map((m) => ({
      value: String(m.user?.id || m.user_id || ""),
      label: memberDisplayName(m),
    })),
  ];

  return (
    <section
      className="clients-page feature-company-workspace feature-company-clients"
      id="Csection"
      tabIndex="-1"
    >
      <div className="container">
        <CompanyPageHeader
          title="Черга без відповідального"
          subtitle="Призначення OWNER/ADMIN"
          company={authorityMatches ? company : null}
          membership={authorityMatches ? membership : null}
          companyId={companyId}
          activeNav="clients"
          actions={
            <Link
              to={`/companies/${companyId}/clients`}
              className="action-btn action-btn--ghost"
            >
              До списку
            </Link>
          }
        />

        {state === "loading" ? (
          <div className="feature-company-workspace__state">
            <Spinner />
          </div>
        ) : null}
        {state === "unavailable" ? (
          <EmptyState
            title="Корпоративні клієнти недоступні"
            description="Черга не завантажується, поки функція вимкнена."
          />
        ) : null}
        {state === "denied" ? (
          <EmptyState
            title="Немає доступу"
            description="Черга доступна лише OWNER/ADMIN при активному членстві."
          />
        ) : null}
        {state === "not-found" ? (
          <EmptyState title="Не знайдено" description="Ресурс недоступний." />
        ) : null}
        {state === "error" ? (
          <EmptyState
            title="Помилка"
            description={error || "Спробуйте ще раз."}
            primaryAction={
              <button type="button" className="action-btn" onClick={load}>
                Повторити
              </button>
            }
          />
        ) : null}

        {state === "ready" && canManage ? (
          <div className="clients-workspace">
            {actionError ? (
              <p className="feature-company-workspace__error" role="alert">
                {actionError}
              </p>
            ) : null}
            {rows.length === 0 ? (
              <EmptyState
                title="Черга порожня"
                description="Усі корпоративні клієнти мають відповідального."
              />
            ) : (
              <div className="clients-table-wrap">
                <table className="clients-table">
                  <thead>
                    <tr>
                      <th>Клієнт</th>
                      <th>Тип</th>
                      <th>Статус</th>
                      <th>Призначити</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id} className="client-row">
                        <th scope="row" className="client-row__name">
                          <Link
                            to={`/companies/${companyId}/clients/${row.id}`}
                            className="client-link"
                          >
                            <span className="client-link__text">
                              <span className="client-link__title">
                                {clientDisplayName(row)}
                              </span>
                            </span>
                          </Link>
                        </th>
                        <td data-c2-col="type">{typeLabel(row.type)}</td>
                        <td data-c2-col="status">{statusLabel(row.status)}</td>
                        <td data-c2-col="assign">
                          <div className="feature-company-workspace__inline-actions">
                            <Select
                              name={`assign-${row.id}`}
                              value={assignDraft[row.id] || ""}
                              onChange={(e) =>
                                setAssignDraft((prev) => ({
                                  ...prev,
                                  [row.id]: e.target.value,
                                }))
                              }
                              options={assigneeOptions}
                              disabled={busyId === row.id}
                            />
                            <button
                              type="button"
                              className="action-btn"
                              disabled={
                                busyId === row.id || !assignDraft[row.id]
                              }
                              onClick={() => handleAssign(row.id)}
                            >
                              Призначити
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <NumberedPagination
                  currentPage={page}
                  totalPages={totalPages}
                  hasPrevious={Boolean(prevUrl)}
                  hasNext={Boolean(nextUrl)}
                  ariaLabel="Сторінки черги"
                  onPageChange={(next) => setPage(next)}
                />
              </div>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default CompanyClientsUnassignedContent;
